import { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Lock, Camera, BookOpen, Calendar, Mail, FileText, Settings, LogOut, Users, Film, Fish } from "lucide-react";
import axios from "axios";
import PhotoManager from "./PhotoManager";
import GuestBookManager from "./GuestBookManager";
import BookingManager from "./BookingManager";
import ContentEditor from "./ContentEditor";
import WaitlistManager from "./WaitlistManager";
import VideoManager from "./VideoManager";
import WhalesManager from "./WhalesManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("admin_token", res.data.token);
      onLogin(res.data.token);
    } catch {
      setError("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-wc-forest flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Lock className="w-10 h-10 text-wc-brass mx-auto mb-4" />
          <h1 className="font-display text-wc-cream text-2xl font-bold">Whale Cove Admin</h1>
          <p className="font-body text-wc-driftwood/60 text-sm mt-1">Enter your admin password</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="bg-wc-forest border-wc-teal/30 text-wc-cream placeholder:text-wc-driftwood/40 focus:ring-wc-teal"
            data-testid="admin-password-input"
          />
          {error && <p className="text-red-400 text-sm font-body">{error}</p>}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-wc-teal text-wc-cream hover:bg-wc-brass font-body"
            data-testid="admin-login-button"
          >
            {loading ? "Checking..." : "Sign In"}
          </Button>
        </form>
        <a href="/" className="block text-center mt-6 font-body text-wc-driftwood/40 text-xs hover:text-wc-driftwood transition-colors">
          Back to site
        </a>
      </div>
    </div>
  );
}

function AdminDashboard({ token, onLogout }) {
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Top bar */}
      <div className="bg-wc-forest text-wc-cream px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="font-display text-lg font-bold">Whale Cove Admin</h1>
          <a href="/" className="font-body text-wc-driftwood/50 text-xs hover:text-wc-cream transition-colors">
            View Site
          </a>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onLogout}
          className="text-wc-driftwood/60 hover:text-wc-cream hover:bg-wc-teal/20"
          data-testid="admin-logout-button"
        >
          <LogOut className="w-4 h-4 mr-1" /> Sign Out
        </Button>
      </div>

      {/* Dashboard tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <Tabs defaultValue="photos">
          <TabsList className="bg-white border border-gray-200 mb-6 flex-wrap h-auto p-1 gap-1">
            <TabsTrigger value="photos" className="font-body text-sm data-[state=active]:bg-wc-teal data-[state=active]:text-white gap-1.5">
              <Camera className="w-4 h-4" /> Photos
            </TabsTrigger>
            <TabsTrigger value="videos" className="font-body text-sm data-[state=active]:bg-wc-teal data-[state=active]:text-white gap-1.5" data-testid="admin-tab-videos">
              <Film className="w-4 h-4" /> Videos
            </TabsTrigger>
            <TabsTrigger value="whales" className="font-body text-sm data-[state=active]:bg-wc-teal data-[state=active]:text-white gap-1.5" data-testid="admin-tab-whales">
              <Fish className="w-4 h-4" /> Whales
            </TabsTrigger>
            <TabsTrigger value="guestbook" className="font-body text-sm data-[state=active]:bg-wc-teal data-[state=active]:text-white gap-1.5">
              <BookOpen className="w-4 h-4" /> Guest Book
            </TabsTrigger>
            <TabsTrigger value="bookings" className="font-body text-sm data-[state=active]:bg-wc-teal data-[state=active]:text-white gap-1.5">
              <Calendar className="w-4 h-4" /> Bookings
            </TabsTrigger>
            <TabsTrigger value="waitlist" className="font-body text-sm data-[state=active]:bg-wc-teal data-[state=active]:text-white gap-1.5" data-testid="admin-tab-waitlist">
              <Users className="w-4 h-4" /> Waitlist
            </TabsTrigger>
            <TabsTrigger value="contacts" className="font-body text-sm data-[state=active]:bg-wc-teal data-[state=active]:text-white gap-1.5">
              <Mail className="w-4 h-4" /> Contacts
            </TabsTrigger>
            <TabsTrigger value="content" className="font-body text-sm data-[state=active]:bg-wc-teal data-[state=active]:text-white gap-1.5">
              <FileText className="w-4 h-4" /> Content
            </TabsTrigger>
            <TabsTrigger value="settings" className="font-body text-sm data-[state=active]:bg-wc-teal data-[state=active]:text-white gap-1.5">
              <Settings className="w-4 h-4" /> Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="photos">
            <PhotoManager authHeader={authHeader} />
          </TabsContent>
          <TabsContent value="videos">
            <VideoManager authHeader={authHeader} />
          </TabsContent>
          <TabsContent value="whales">
            <WhalesManager authHeader={authHeader} />
          </TabsContent>
          <TabsContent value="guestbook">
            <GuestBookManager authHeader={authHeader} />
          </TabsContent>
          <TabsContent value="bookings">
            <BookingManager authHeader={authHeader} type="bookings" />
          </TabsContent>
          <TabsContent value="waitlist">
            <WaitlistManager authHeader={authHeader} />
          </TabsContent>
          <TabsContent value="contacts">
            <BookingManager authHeader={authHeader} type="contacts" />
          </TabsContent>
          <TabsContent value="content">
            <ContentEditor authHeader={authHeader} />
          </TabsContent>
          <TabsContent value="settings">
            <ContentEditor authHeader={authHeader} settingsMode />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(null);
  const [checking, setChecking] = useState(true);

  const verifyToken = useCallback(async (t) => {
    try {
      await axios.get(`${API}/admin/verify`, { headers: { Authorization: `Bearer ${t}` } });
      setToken(t);
    } catch {
      localStorage.removeItem("admin_token");
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("admin_token");
    if (saved) verifyToken(saved);
    else setChecking(false);
  }, [verifyToken]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
  };

  if (checking) {
    return <div className="min-h-screen bg-wc-forest flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-wc-brass border-t-transparent rounded-full animate-spin" />
    </div>;
  }

  if (!token) return <AdminLogin onLogin={setToken} />;
  return <AdminDashboard token={token} onLogout={handleLogout} />;
}
